import type { UploadFile } from "antd";
import { getFileExtension } from "pages/media/utils";
import type { ProjectDocument } from "pages/media/types";
import { DEFAULT_EXT_STYLE, EXT_STYLES } from "./consts";
import type { ExtStyle } from "./types";
import type {
  DocumentFile,
  DocumentFileExt,
} from "../components/config-cards/types";

/** Append items whose key isn't already present. Returns the same array (by
 * reference) when nothing is new, so callers can skip a redundant onChange. */
export const dedupeAppend = <T>(
  current: T[],
  incoming: T[],
  getKey: (item: T) => string
): T[] => {
  const seen = new Set(current.map(getKey));
  const added = incoming.filter((item) => !seen.has(getKey(item)));
  return added.length > 0 ? [...current, ...added] : current;
};

/** Remove the item matching `key`. */
export const removeByKey = <T>(
  current: T[],
  key: string,
  getKey: (item: T) => string
): T[] => current.filter((item) => getKey(item) !== key);

/** Move the item at `from` to `to` (order matters — first is primary). */
export const reorder = <T>(current: T[], from: number, to: number): T[] => {
  if (from === to) return current;
  const next = [...current];
  const [moved] = next.splice(from, 1);
  if (moved) next.splice(to, 0, moved);
  return next;
};

/** Human-readable file size. Empty string for missing/zero bytes. */
export const formatFileSize = (bytes?: number): string => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** Badge color + background for a file, keyed by its (lowercased) extension. */
export const extStyle = (name: string): ExtStyle =>
  EXT_STYLES[getFileExtension(name)] ?? DEFAULT_EXT_STYLE;

// ─── Shared UploadFile mappers ───────────────────────────────────────────────
// The document list is one shared `UploadFile[]` (see PlaybookDraftContext),
// consumed by both the Step-1 sidebar and the customize-view Documents card.
// These map to/from that canonical shape so the two UIs can't drift apart.

/** Wrap a browser `File` in antd's `UploadFile`, keeping `originFileObj` so the
 *  file can be uploaded to S3 later (the create flow reads that field). */
export const toUploadFile = (
  file: File,
  index: number,
  uidPrefix = "create-experience"
): UploadFile => ({
  uid: `${uidPrefix}-${Date.now()}-${index}`,
  name: file.name,
  size: file.size,
  type: file.type,
  originFileObj: file as unknown as UploadFile["originFileObj"],
});

/** Single dedup / removal / hidden-tracking identity for a document. Same file
 *  (name + size) can't be added twice, so name+size uniquely identifies a row. */
export const uploadFileKey = (f: UploadFile): string =>
  `${f.name}:${f.size ?? 0}`;

/** Map a filename to one of the three document badge types we render. */
const extFromName = (name: string): DocumentFileExt => {
  const ext = getFileExtension(name);
  if (ext === "ppt" || ext === "pptx") return "ppt";
  if (ext === "doc" || ext === "docx") return "doc";
  return "pdf";
};

/** Build the presentation-only `DocumentFile` (id/name/size/ext/hidden) the
 *  Documents card's row + preview components render, from a canonical
 *  `UploadFile` and its current hidden state. `previewUrl` is a local
 *  `blob:` URL (see `DocumentsConfigCard`) — the file hasn't been uploaded
 *  yet, so there's nothing else to preview it from. */
export const toDocumentFilePresentation = (
  f: UploadFile,
  hidden: boolean,
  previewUrl?: string
): DocumentFile => ({
  id: uploadFileKey(f),
  name: f.name,
  size: formatFileSize(f.size),
  ext: extFromName(f.name),
  hidden,
  previewUrl,
});

/** Build the presentation-only `DocumentFile` from an already-persisted
 *  `ProjectDocument` (the Edit Experience page's documents), so the shared
 *  row/preview components can render both flows without drifting apart.
 *  `path` is the real, persisted asset URL — always previewable. */
export const toDocumentFilePresentationFromProject = (
  doc: ProjectDocument
): DocumentFile => ({
  id: doc.id,
  name: doc.name,
  size: "",
  ext: extFromName(doc.name),
  hidden: doc.hidden,
  previewUrl: doc.path,
});

/**
 * The document types this flow accepts (source files an experience is generated
 * from). Deliberately NOT the backend `primary_mime_types` config: that set is
 * broader (it includes images, which the create flow must reject here). Used for
 * BOTH the `<input accept>` and the drag-drop validation so browse and drop agree.
 */
export const DOCUMENT_ACCEPT = ".pdf,.doc,.docx,.ppt,.pptx" as const;

/** Does a file satisfy an HTML `accept` string (comma-separated extensions
 *  and/or MIME types, e.g. ".pdf, application/pdf")? Mirrors how the browser
 *  evaluates `<input accept>`, so drag-drop (which ignores that attribute) can
 *  be validated the same way the file picker already is. An empty `accept`
 *  accepts everything. */
export const matchesAccept = (file: File, accept: string): boolean => {
  const tokens = accept
    .split(",")
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean);
  if (tokens.length === 0) return true;

  const name = file.name.toLowerCase();
  const mime = file.type.toLowerCase();

  return tokens.some((token) => {
    if (token.startsWith(".")) return name.endsWith(token);
    if (token.endsWith("/*")) return mime.startsWith(token.slice(0, -1));
    return mime === token;
  });
};
